/**
 * Wires `@sonauto/offline-sync` to this app's API client.
 *
 * The package owns the queue, the policy and the backoff; this file owns the
 * transport (how a queued record becomes an HTTP call) and the "when to
 * replay" triggers. Keeping them apart is what lets the outbox be unit-tested
 * without a fetch impl.
 *
 * REPLAY TRIGGERS
 *   - the browser fires `online`;
 *   - a 20-second heartbeat, because `navigator.onLine` lies on captive
 *     portals and on wifi that is associated but has no route;
 *   - the tab becomes visible again;
 *   - an explicit tap on the sync pill.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { isApiError } from '@sonauto/api-client';
import {
  enqueue,
  listAll,
  pruneSynced,
  replay,
  discard,
  retryNow,
  snapshot,
  syncStateLabel,
} from '@sonauto/offline-sync';
import type { EnqueueInput, OutboxRecord, ReplayOutcome, SyncSnapshot } from '@sonauto/offline-sync';
import { useAuth } from '../auth/AuthProvider';
import { useOnline } from '../lib/hooks';

const HEARTBEAT_MS = 20_000;

export interface OutboxState {
  readonly snapshot: SyncSnapshot;
  readonly records: readonly OutboxRecord[];
  readonly label: string;
  /** Queue an operation. Throws if `@sonauto/offline-sync`'s policy forbids it. */
  readonly queue: (input: EnqueueInput) => Promise<OutboxRecord>;
  readonly flush: () => Promise<void>;
  readonly discardRecord: (id: string) => Promise<void>;
  readonly retryRecord: (id: string) => Promise<void>;
  readonly refresh: () => void;
}

const OutboxContext = createContext<OutboxState | null>(null);

export function useOutbox(): OutboxState {
  const ctx = useContext(OutboxContext);
  if (ctx === null) throw new Error('useOutbox phải được dùng bên trong <OutboxProvider>.');
  return ctx;
}

const IDLE: SyncSnapshot = {
  state: 'ONLINE',
  pendingCount: 0,
  failedCount: 0,
  conflictCount: 0,
  lastSyncedAt: null,
  online: true,
};

export function OutboxProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const { client, signedIn } = useAuth();
  const online = useOnline();
  const [snap, setSnap] = useState<SyncSnapshot>(IDLE);
  const [records, setRecords] = useState<readonly OutboxRecord[]>([]);
  const replaying = useRef(false);

  const refreshState = useCallback(async (): Promise<void> => {
    try {
      const [s, all] = await Promise.all([snapshot(online), listAll()]);
      setSnap(s);
      setRecords(all.filter((r) => r.status !== 'SYNCED'));
    } catch {
      // IndexedDB unavailable (private mode / locked-down kiosk). The app still
      // works online; the pill just never shows a queue.
      setSnap({ ...IDLE, online, state: online ? 'ONLINE' : 'OFFLINE' });
    }
  }, [online]);

  /**
   * Turn one queued record into an HTTP call.
   *
   * The record's OWN idempotency key is reused (`idempotencyKey: record.id`
   * equivalent) so the server replays the original response instead of
   * performing the operation twice.
   */
  const transport = useCallback(
    async (record: OutboxRecord): Promise<ReplayOutcome> => {
      try {
        await client.request(record.request.method, record.request.path, {
          body: record.request.body,
          idempotencyKey: record.idempotencyKey,
          reason: record.request.reason ?? null,
        });
        return { ok: true };
      } catch (cause: unknown) {
        if (isApiError(cause)) {
          // `retryable` comes from the API's own error envelope; 5xx / 429 /
          // network are retryable, a 409 "bàn đã đóng" is not.
          return {
            ok: false,
            retryable: cause.retryable,
            status: cause.status,
            message: cause.message,
          };
        }
        return {
          ok: false,
          retryable: true,
          status: null,
          message: cause instanceof Error ? cause.message : 'Lỗi mạng',
        };
      }
    },
    [client],
  );

  const flush = useCallback(async (): Promise<void> => {
    if (replaying.current || !signedIn) return;
    replaying.current = true;
    try {
      await replay(transport);
      await pruneSynced();
    } catch {
      /* the queue is intact; the next trigger tries again */
    } finally {
      replaying.current = false;
      await refreshState();
    }
  }, [refreshState, signedIn, transport]);

  useEffect(() => {
    void refreshState();
  }, [refreshState]);

  useEffect(() => {
    if (!online) return undefined;
    void flush();
    const id = window.setInterval(() => void flush(), HEARTBEAT_MS);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void flush();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [flush, online]);

  const queue = useCallback(
    async (input: EnqueueInput): Promise<OutboxRecord> => {
      const record = await enqueue(input);
      await refreshState();
      // Try immediately: most "offline" taps happen during a two-second blip.
      void flush();
      return record;
    },
    [flush, refreshState],
  );

  const value = useMemo<OutboxState>(
    () => ({
      snapshot: snap,
      records,
      label: syncStateLabel(snap.state),
      queue,
      flush,
      discardRecord: async (id: string) => {
        await discard(id);
        await refreshState();
      },
      retryRecord: async (id: string) => {
        await retryNow(id);
        await flush();
      },
      refresh: () => void refreshState(),
    }),
    [snap, records, queue, flush, refreshState],
  );

  return <OutboxContext.Provider value={value}>{children}</OutboxContext.Provider>;
}
